// stores/_base/createIndexedDBStore.ts

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import { getUnifiedDB, isIndexedDBSupported } from '@/utils/db/unifiedIndexedDB'
import { Timestamp } from '@farmfree/common/firebase/firebaseTypes'
import { fromServerData } from '@/utils/firestore/fromServerData'

/**
 * IndexedDB 저장소 팩토리 설정 옵션
 */
export interface IndexedDBStoreConfig<T extends { id: string }> {
  /** 스토어 고유 ID */
  storeId: string

  /** IndexedDB 테이블명 */
  tableName: string

  /** 버전 필드명 (Company 문서 내) */
  versionField: string

  /** 데이터 서비스 */
  service: {
    /** 수정된 데이터 조회 */
    getModified: (companyId: string, since: number) => Promise<ApiResponse<T[]>>

    /** 삭제된 데이터 ID 조회 */
    getDeleted: (companyId: string) => Promise<ApiResponse<string[]>>

    /** 단일 항목 저장 */
    saveItem?: (companyId: string, item: T) => Promise<ApiResponse<{ id: string }>>

    /** 다중 항목 저장 */
    saveItems?: (companyId: string, items: T[]) => Promise<ApiResponse>

    /** 항목 삭제 */
    deleteItem?: (companyId: string, id: string) => Promise<ApiResponse>
  }

  /** 옵션 설정 */
  options?: {
    /** 디버그 로깅 활성화 */
    debug?: boolean
  }
}

/**
 * IndexedDB 기반 버전 관리 스토어 생성
 *
 * @description
 * - 대용량 데이터를 IndexedDB에 저장하여 localStorage 한계 극복
 * - 버전 기반 증분 동기화로 네트워크 효율성 극대화
 * - 타입 안전성과 일관된 API 제공
 *
 * @example
 * ```typescript
 * export const useProductStore = createIndexedDBStore({
 *   storeId: 'product',
 *   tableName: 'products',
 *   versionField: 'productVersion',
 *   service: {
 *     getModified: (companyId, since) => productService.getModified(companyId, since),
 *     getDeleted: (companyId) => productService.getDeleted(companyId),
 *     saveItem: (companyId, item) => productService.saveItem(companyId, item),
 *     deleteItem: (companyId, id) => productService.deleteItem(companyId, id)
 *   }
 * })
 * ```
 */
export function createIndexedDBStore<T extends { id: string }>(config: IndexedDBStoreConfig<T>) {
  const { storeId, tableName, versionField, service, options = {} } = config
  const { debug = false } = options

  return defineStore(storeId, () => {
    // ==================== State ====================
    const items = ref<T[]>([])
    const loading = ref(false)
    const error = ref<string | null>(null)
    const lastSyncTime = ref(0)
    const currentVersion = ref(0)

    // 현재 로드된 회사 ID
    let loadedCompanyId: string | null = null

    // ==================== Computed ====================
    const itemCount = computed(() => items.value.length)
    const hasData = computed(() => items.value.length > 0)

    // ==================== Private Helpers ====================

    /**
     * Timestamp 객체를 { seconds, nanoseconds } 형태로 변환 (IndexedDB 저장용)
     * 복원 시 fromServerData와 동일한 방식으로 Timestamp 객체로 변환 가능
     */
    function sanitizeForStorage(item: any): any {
      if (!item) return item

      if (Array.isArray(item)) {
        return item.map(sanitizeForStorage)
      }

      if (typeof item === 'object') {
        // 이미 { seconds, nanoseconds } 형태면 그대로 유지
        if (
          'seconds' in item &&
          'nanoseconds' in item &&
          typeof item.seconds === 'number' &&
          typeof item.nanoseconds === 'number' &&
          Object.keys(item).length === 2
        ) {
          return { seconds: item.seconds, nanoseconds: item.nanoseconds }
        }

        const sanitized: any = {}
        for (const key in item) {
          const value = item[key]
          // Timestamp 객체 처리 → { seconds, nanoseconds } 형태로 저장
          if (value && typeof value === 'object' && 'toMillis' in value && 'seconds' in value) {
            sanitized[key] = { seconds: value.seconds, nanoseconds: value.nanoseconds }
          }
          // Date 객체 처리 → { seconds, nanoseconds } 형태로 저장
          else if (value instanceof Date) {
            const millis = value.getTime()
            sanitized[key] = {
              seconds: Math.floor(millis / 1000),
              nanoseconds: (millis % 1000) * 1000000,
            }
          }
          // 중첩 객체 처리 (재귀적으로 변환)
          else if (value && typeof value === 'object') {
            sanitized[key] = sanitizeForStorage(value)
          } else {
            sanitized[key] = value
          }
        }
        return sanitized
      }

      return item
    }

    /**
     * IndexedDB에서 불러온 데이터 복원
     * { seconds, nanoseconds } 형태 → Timestamp 객체로 변환
     * fromServerData와 동일한 로직 사용
     */
    function restoreFromStorage(item: any): any {
      return fromServerData(item)
    }

    /**
     * 로그 출력 (디버그 모드)
     */
    function log(...args: any[]) {
      if (debug) {
        console.log(`[${storeId}]`, ...args)
      }
    }

    // ==================== Public Actions ====================

    /**
     * IndexedDB에서 캐시 복원 (비동기)
     * handleCompanyChange에서 호출
     */
    async function initializeCache(companyId: string): Promise<void> {
      console.log(
        `🔍 IndexedDB Debug - [${storeId}] initializeCache 시작 - companyId: ${companyId}`
      )

      if (!companyId) {
        log('회사 ID 없음')
        return
      }

      // 회사가 변경되었으면 메모리 초기화
      if (loadedCompanyId !== companyId) {
        log(`회사 변경 감지: ${loadedCompanyId} -> ${companyId}`)
        items.value = []
        lastSyncTime.value = 0
        currentVersion.value = 0
        loadedCompanyId = companyId
      } else if (items.value.length > 0) {
        // 같은 회사이고 메모리에 데이터가 있으면 재사용
        log(`메모리 캐시 사용: ${items.value.length}개`)
        return
      }

      loading.value = true
      error.value = null

      try {
        // 통합 IndexedDB에서 로드
        const db = getUnifiedDB()
        console.log(`🔍 IndexedDB Debug - [${storeId}] 조회 시작 - tableName: ${tableName}`)
        const cachedData = await db.loadStoreData<T>(companyId, tableName)
        console.log(
          `🔍 IndexedDB Debug - [${storeId}] 조회 결과:`,
          cachedData ? `${cachedData.items.length}개 항목` : 'null'
        )

        if (cachedData && cachedData.items.length > 0) {
          // IndexedDB에서 불러온 데이터의 날짜 필드를 Timestamp 객체로 복원
          items.value = restoreFromStorage(cachedData.items) as T[]
          lastSyncTime.value = cachedData.lastSync
          currentVersion.value = cachedData.version
          log(`IndexedDB 캐시 복원: ${cachedData.items.length}개`)
          console.log(`🔍 IndexedDB Debug - [${storeId}] 첫 번째 아이템:`, cachedData.items[0])

          // Store items 확인
          console.log(
            `🔴 ORDER-FLOW - [${storeId}] initializeCache 후 items.value 길이:`,
            items.value.length
          )
          if (storeId === 'activeOrder' && items.value.length > 0) {
            console.log(`🔴 ORDER-FLOW - [activeOrder] 첫 번째 item:`, items.value[0])

            // IndexedDB에 저장된 원본 데이터를 자세히 출력
            console.log(`🔴🔴🔴 ORDER-FLOW - [activeOrder] IndexedDB 원본 데이터 (전체):`)
            console.log(JSON.stringify(items.value[0], null, 2))
          }
        } else {
          log('캐시 없음, 서버 동기화 필요')
          console.log(`🔍 IndexedDB Debug - [${storeId}] 캐시 없음, 서버에서 로드 시작`)
          // 초기 로드 시 동기화
          await syncWithServer(companyId)
        }
      } catch (err) {
        console.error(`[${storeId}] 캐시 초기화 실패:`, err)
        error.value = '캐시 초기화 실패'
        // 캐시 실패 시 서버에서 로드
        await syncWithServer(companyId)
      } finally {
        loading.value = false
      }
    }

    /**
     * 서버와 동기화
     * @param companyId 회사 ID
     */
    async function syncWithServer(companyId: string): Promise<ApiResponse> {
      if (!companyId) {
        return { isSuccess: false, message: '회사 정보 없음' }
      }

      loading.value = true
      error.value = null

      try {
        const since = lastSyncTime.value || 0

        log(`동기화 시작 (since: ${since})`)

        // 병렬로 삭제/수정 데이터 조회
        const [resDeleted, resModified] = await Promise.all([
          service.getDeleted(companyId),
          service.getModified(companyId, since),
        ])

        if (!resDeleted.isSuccess) {
          throw new Error(resDeleted.message || '삭제 데이터 조회 실패')
        }
        if (!resModified.isSuccess) {
          throw new Error(resModified.message || '수정 데이터 조회 실패')
        }

        const deletedIds = new Set(resDeleted.data || [])
        const modifiedItems = resModified.data || []
        const modifiedIds = new Set(modifiedItems.map((item) => item.id))

        // 기존 데이터에서 삭제/수정된 항목 제외
        const keptItems = items.value.filter(
          (item) => !deletedIds.has(item.id) && !modifiedIds.has(item.id)
        )

        // 새 데이터 병합 (유지 + 수정/추가)
        const newItems = [...keptItems, ...modifiedItems] as T[]
        items.value = newItems

        // 통합 IndexedDB에 저장 (Timestamp 객체 sanitize)
        const db = getUnifiedDB()
        await db.saveStoreData(companyId, tableName, {
          items: sanitizeForStorage(newItems),
          version: currentVersion.value + 1,
          lastSync: Date.now(),
        })

        lastSyncTime.value = Date.now()
        currentVersion.value++

        log(
          `동기화 완료: ${newItems.length}개 (삭제: ${deletedIds.size}, 수정/추가: ${modifiedItems.length})`
        )

        return {
          isSuccess: true,
          data: newItems,
          message: `${newItems.length}개 항목 동기화 완료`,
        }
      } catch (err: any) {
        console.error(`[${storeId}] 동기화 실패:`, err)
        error.value = err.message || '서버 동기화 실패'
        return {
          isSuccess: false,
          message: error.value || undefined,
        }
      } finally {
        loading.value = false
      }
    }

    /**
     * 단일 항목 저장 (동기화 의존 방식)
     *
     * 새 항목: Firestore 저장 → Company 버전 업데이트 → watchCompanyRealtime이 자동 동기화
     * 기존 항목: Firestore 저장 → 즉시 로컬 업데이트
     */
    async function saveItem(companyId: string, item: T): Promise<ApiResponse<{ id: string }>> {
      if (!service.saveItem) {
        return { isSuccess: false, message: 'saveItem 서비스 없음' }
      }
      if (!companyId) {
        return { isSuccess: false, message: '회사 정보 없음' }
      }

      // 새 항목인지 확인 (id가 없거나 빈 문자열)
      const isNewItem = !item.id || item.id === ''

      try {
        // 1. 서버에 저장
        const itemToSend = isNewItem ? { ...item, id: '' } : item
        const response = await service.saveItem(companyId, itemToSend as T)

        if (!response.isSuccess || !response.data?.id) {
          return response
        }

        // 2. 기존 항목 수정인 경우 즉시 로컬 업데이트
        if (!isNewItem) {
          const index = items.value.findIndex((p) => p.id === item.id)
          if (index >= 0) {
            const newItems = [...items.value] as T[]
            newItems[index] = { ...item } as T
            items.value = newItems

            // IndexedDB도 업데이트
            const db = getUnifiedDB()
            await db.saveStoreData(companyId, tableName, {
              items: sanitizeForStorage(items.value),
              version: currentVersion.value,
              lastSync: lastSyncTime.value,
            })
            log(`기존 항목 업데이트: ${item.id}`)
          }
        } else {
          // 3. 새 항목인 경우 동기화를 통해 자동으로 추가됨
          log(`새 항목 저장 완료: ${response.data.id} (동기화 대기 중)`)
        }

        return response
      } catch (err: any) {
        console.error(`[${storeId}] 항목 저장 실패:`, err)
        return {
          isSuccess: false,
          message: err.message || '항목 저장 실패',
        }
      }
    }

    /**
     * 다중 항목 저장
     */
    async function saveItems(companyId: string, itemList: T[]): Promise<ApiResponse> {
      if (!service.saveItems) {
        return { isSuccess: false, message: 'saveItems 서비스 없음' }
      }
      if (!companyId) {
        return { isSuccess: false, message: '회사 정보 없음' }
      }

      try {
        const response = await service.saveItems(companyId, itemList)
        if (!response.isSuccess) {
          return response
        }

        // 메모리 업데이트 (Map으로 효율적 처리)
        const itemMap = new Map<string, T>()

        // 기존 항목 추가
        for (const item of items.value) {
          itemMap.set(item.id, item as T)
        }

        // 새 항목 추가/업데이트
        for (const item of itemList) {
          itemMap.set(item.id, item)
        }

        items.value = Array.from(itemMap.values()) as T[]

        // 통합 IndexedDB 업데이트 (Timestamp 객체 sanitize)
        const db = getUnifiedDB()
        await db.saveStoreData(companyId, tableName, {
          items: sanitizeForStorage(items.value),
          version: currentVersion.value,
          lastSync: lastSyncTime.value,
        })

        log(`${itemList.length}개 항목 저장`)
        return response
      } catch (err: any) {
        console.error(`[${storeId}] 다중 항목 저장 실패:`, err)
        return {
          isSuccess: false,
          message: err.message || '다중 항목 저장 실패',
        }
      }
    }

    /**
     * 항목 삭제
     */
    async function deleteItem(companyId: string, id: string): Promise<ApiResponse> {
      if (!service.deleteItem) {
        return { isSuccess: false, message: 'deleteItem 서비스 없음' }
      }
      if (!companyId) {
        return { isSuccess: false, message: '회사 정보 없음' }
      }

      try {
        const response = await service.deleteItem(companyId, id)
        if (!response.isSuccess) {
          return response
        }

        // 메모리에서 삭제
        items.value = items.value.filter((item) => item.id !== id) as T[]

        // 통합 IndexedDB 업데이트 (Timestamp 객체 sanitize)
        const db = getUnifiedDB()
        await db.saveStoreData(companyId, tableName, {
          items: sanitizeForStorage(items.value),
          version: currentVersion.value,
          lastSync: lastSyncTime.value,
        })

        log(`항목 삭제: ${id}`)
        return response
      } catch (err: any) {
        console.error(`[${storeId}] 항목 삭제 실패:`, err)
        return {
          isSuccess: false,
          message: err.message || '항목 삭제 실패',
        }
      }
    }

    /**
     * 전체 데이터 초기화 (메모리 + IndexedDB)
     */
    async function clearAll(): Promise<void> {
      items.value = []
      lastSyncTime.value = 0
      currentVersion.value = 0
      error.value = null
      loadedCompanyId = null

      const db = getUnifiedDB()
      await db.clearTableData(tableName)

      log('전체 데이터 초기화 (메모리 + IndexedDB)')
    }

    /**
     * 특정 회사 데이터 초기화
     */
    async function clearCompanyData(companyId: string): Promise<void> {
      if (!companyId) return

      items.value = []
      lastSyncTime.value = 0
      currentVersion.value = 0
      error.value = null

      if (loadedCompanyId === companyId) {
        loadedCompanyId = null
      }

      const db = getUnifiedDB()
      await db.clearStoreData(companyId, tableName)

      log(`회사 데이터 초기화: ${companyId}`)
    }

    /**
     * 전체 재동기화 (스키마 변경 시)
     */
    async function syncFromScratch(companyId: string): Promise<ApiResponse> {
      log('전체 재동기화 시작')
      items.value = []
      lastSyncTime.value = 0
      currentVersion.value = 0
      return await syncWithServer(companyId)
    }

    /**
     * ID로 항목 조회
     */
    function getItemById(id: string): T | undefined {
      return items.value.find((item) => item.id === id) as T | undefined
    }

    /**
     * 조건에 맞는 항목들 조회
     */
    function getItemsBy(predicate: (item: T) => boolean): T[] {
      return items.value.filter(predicate as any) as T[]
    }

    /**
     * localStorage 호환성을 위한 빈 메서드
     * 새로운 코드에서는 initializeCache를 사용
     */
    function restoreCache(companyId: string): void {
      // IndexedDB는 비동기로 처리되므로 빈 함수로 두고
      // initializeCache에서 실제 복원 처리
      log('restoreCache called - use initializeCache instead')
    }

    /**
     * localStorage 호환성을 위한 allItems getter
     */
    function allItems(): T[] {
      return items.value as T[]
    }

    // ==================== Return ====================
    // items 반환 전 확인
    console.log(`🔴 ORDER-FLOW - [${storeId}] Store 생성 시 items 참조:`, items)
    console.log(`🔴 ORDER-FLOW - [${storeId}] Store 생성 시 items.value 길이:`, items.value.length)

    return {
      // State
      items,
      loading,
      error,
      lastSyncTime,
      currentVersion,

      // Computed
      itemCount,
      hasData,

      // Actions
      initializeCache,
      syncWithServer,
      syncFromScratch,
      saveItem,
      saveItems,
      deleteItem,
      clearAll,
      clearCompanyData,
      getItemById,
      getItemsBy,

      // Legacy compatibility
      restoreCache,
      allItems,
    }
  })
}
